import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { machineService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Plus, Search, Filter, Pencil, Trash2, X, ExternalLink, Cog } from 'lucide-react';

const STATUS_MAP = {
  attiva: { label: 'Attiva', cls: 'bg-green-100 text-green-700' },
  fermata: { label: 'Fermata', cls: 'bg-red-100 text-red-700' },
  manutenzione: { label: 'In Manutenzione', cls: 'bg-amber-100 text-amber-700' },
};

export default function MachinesPage() {
  const { user } = useAuth();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ machine_type: '', department: '', status: '', manufacturer: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [types, setTypes] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({ machine_code: '', machine_name: '', machine_type: '', department: '', manufacturer: '', model: '', serial_number: '', location: '', status: 'attiva' });
  const canEdit = ['admin', 'production', 'management'].includes(user?.role);

  const load = async () => {
    try {
      setLoading(true);
      const params = { ...filters };
      if (search) params.search = search;
      Object.keys(params).forEach(k => { if (!params[k]) delete params[k]; });
      const res = await machineService.getAll(params);
      setMachines(res.data);
    } catch { toast.error('Errore caricamento macchine'); } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [search, filters]);
  useEffect(() => {
    Promise.all([machineService.getTypes(), machineService.getManufacturers(), machineService.getDepartments()])
      .then(([t, m, d]) => { setTypes(t.data); setManufacturers(m.data); setDepartments(d.data); }).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ machine_code: '', machine_name: '', machine_type: '', department: '', manufacturer: '', model: '', serial_number: '', location: '', status: 'attiva' });
    setShowModal(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ machine_code: m.machine_code, machine_name: m.machine_name, machine_type: m.machine_type || '', department: m.department || '', manufacturer: m.manufacturer || '', model: m.model || '', serial_number: m.serial_number || '', location: m.location || '', status: m.status });
    setShowModal(true);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.machine_code || !form.machine_name) { toast.error('Codice e nome macchina obbligatori'); return; }
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      payload.machine_code = form.machine_code;
      payload.machine_name = form.machine_name;
      payload.status = form.status || 'attiva';
      if (editing) { await machineService.update(editing.id, payload); toast.success('Macchina aggiornata'); }
      else { await machineService.create(payload); toast.success('Macchina creata'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Errore'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa macchina?')) return;
    try { await machineService.delete(id); toast.success('Macchina eliminata'); load(); } catch { toast.error('Errore eliminazione'); }
  };
  const hasFilters = Object.values(filters).some(v => v);

  return (
    <div className="space-y-4" data-testid="machines-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="machines-title">Anagrafica Macchine</h1>
          <p className="text-sm text-slate-500 mt-0.5">{machines.length} macchine</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input type="text" placeholder="Cerca macchina..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg w-64 focus:ring-2 focus:ring-blue-500" data-testid="machine-search" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} data-testid="machine-filters-btn"
            className={hasFilters ? 'border-blue-300 bg-blue-50 text-blue-700' : ''}>
            <Filter className="w-4 h-4 mr-1" />Filtri
          </Button>
          {canEdit && <Button size="sm" onClick={openCreate} data-testid="new-machine-btn"><Plus className="w-4 h-4 mr-1" />Nuova Macchina</Button>}
        </div>
      </div>

      {showFilters && (
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="machine-filters-panel">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Tipo</label>
              <select value={filters.machine_type} onChange={e => setFilters(f => ({ ...f, machine_type: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-machine-type">
                <option value="">Tutti</option>
                {types.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Reparto</label>
              <select value={filters.department} onChange={e => setFilters(f => ({ ...f, department: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-department">
                <option value="">Tutti</option>
                {departments.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Stato</label>
              <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-status">
                <option value="">Tutti</option>
                <option value="attiva">Attiva</option>
                <option value="fermata">Fermata</option>
                <option value="manutenzione">In Manutenzione</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Costruttore</label>
              <select value={filters.manufacturer} onChange={e => setFilters(f => ({ ...f, manufacturer: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="filter-manufacturer">
                <option value="">Tutti</option>
                {manufacturers.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
          </div>
          {hasFilters && <div className="mt-3 flex justify-end"><button onClick={() => setFilters({ machine_type: '', department: '', status: '', manufacturer: '' })} className="text-sm text-blue-600 hover:text-blue-800 font-medium">Resetta filtri</button></div>}
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="machines-table">
        {loading ? <div className="p-8 text-center text-slate-400">Caricamento...</div> : machines.length === 0 ? (
          <div className="p-8 text-center text-slate-400"><Cog className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-medium text-slate-500">Nessuna macchina trovata</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <th className="px-4 py-3">Codice</th><th className="px-4 py-3">Nome</th><th className="px-4 py-3">Tipo</th><th className="px-4 py-3">Reparto</th>
                <th className="px-4 py-3">Costruttore</th><th className="px-4 py-3">Modello</th><th className="px-4 py-3">Ubicazione</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {machines.map(m => (
                  <tr key={m.id} className="hover:bg-slate-50 transition-colors" data-testid={`machine-row-${m.id}`}>
                    <td className="px-4 py-3 font-mono font-medium text-slate-900">{m.machine_code}</td>
                    <td className="px-4 py-3"><Link to={`/machines/${m.id}`} className="text-blue-600 hover:text-blue-800 font-medium">{m.machine_name}</Link></td>
                    <td className="px-4 py-3 text-slate-600">{m.machine_type || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{m.department || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{m.manufacturer || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{m.model || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{m.location || '-'}</td>
                    <td className="px-4 py-3"><span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_MAP[m.status]?.cls || 'bg-slate-100 text-slate-600'}`}>{STATUS_MAP[m.status]?.label || m.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 justify-end">
                        <Link to={`/machines/${m.id}`} className="p-1 text-slate-400 hover:text-blue-600 rounded"><ExternalLink className="w-4 h-4" /></Link>
                        {canEdit && <button onClick={() => openEdit(m)} className="p-1 text-slate-400 hover:text-blue-600 rounded" data-testid={`edit-machine-${m.id}`}><Pencil className="w-4 h-4" /></button>}
                        {user?.role === 'admin' && <button onClick={() => handleDelete(m.id)} className="p-1 text-slate-400 hover:text-red-600 rounded" data-testid={`delete-machine-${m.id}`}><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4" data-testid="machine-modal">
          <div className="flex min-h-full items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-bold text-slate-900">{editing ? 'Modifica Macchina' : 'Nuova Macchina'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Codice *</label>
                  <input type="text" value={form.machine_code} onChange={e => setForm(f => ({ ...f, machine_code: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" required data-testid="form-machine-code" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Nome *</label>
                  <input type="text" value={form.machine_name} onChange={e => setForm(f => ({ ...f, machine_name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" required data-testid="form-machine-name" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Tipo</label>
                  <input type="text" value={form.machine_type} onChange={e => setForm(f => ({ ...f, machine_type: e.target.value }))} list="type-list" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-machine-type" />
                  <datalist id="type-list">{types.map(t => <option key={t} value={t} />)}</datalist></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Reparto</label>
                  <input type="text" value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} list="dept-list" className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-department" />
                  <datalist id="dept-list">{departments.map(d => <option key={d} value={d} />)}</datalist></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Costruttore</label>
                  <input type="text" value={form.manufacturer} onChange={e => setForm(f => ({ ...f, manufacturer: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-manufacturer" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Modello</label>
                  <input type="text" value={form.model} onChange={e => setForm(f => ({ ...f, model: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-model" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">N. Matricola</label>
                  <input type="text" value={form.serial_number} onChange={e => setForm(f => ({ ...f, serial_number: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-serial" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Ubicazione</label>
                  <input type="text" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-location" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Stato</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg" data-testid="form-status">
                    <option value="attiva">Attiva</option><option value="fermata">Fermata</option><option value="manutenzione">In Manutenzione</option>
                  </select></div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowModal(false)}>Annulla</Button>
                <Button type="submit" size="sm" data-testid="machine-save-btn">{editing ? 'Salva' : 'Crea Macchina'}</Button>
              </div>
            </form>
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
